/**
 * Name normalization + tokenization — the ONE vocabulary shared by the
 * reflection scan and the mapping lexicon (ADR-004 §1 name evidence). A
 * synonym and a rig bone match iff they normalize identically; side markers
 * are token-level, never substrings ('lower' has no 'l' token).
 * Depends on: nothing. Depended on by: mapping/reflection, mapping/map-rig.
 */

export type Side = 'left' | 'right';

/** Split on camelCase boundaries and any non-alphanumeric separator. */
export function tokenize(name: string): string[] {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[^0-9A-Za-z]+/)
    .filter((t) => t.length > 0)
    .map((t) => t.toLowerCase());
}

/** Canonical comparison form: lowercase, separators and case boundaries gone. */
export function normalizeName(name: string): string {
  return tokenize(name).join('');
}

export interface SideToken {
  readonly side: Side;
  /** The name with its side token replaced by '*' — the mirror-pairing key. */
  readonly key: string;
}

/** First side marker: a 'left'/'right' token or a standalone 'l'/'r' token. */
export function sideToken(name: string): SideToken | null {
  const tokens = tokenize(name);
  for (let i = 0; i < tokens.length; i += 1) {
    const t = tokens[i] as string;
    const side: Side | null = t === 'left' || t === 'l' ? 'left' : t === 'right' || t === 'r' ? 'right' : null;
    if (side !== null) {
      return { side, key: tokens.map((tok, j) => (j === i ? '*' : tok)).join('/') };
    }
  }
  return null;
}

/**
 * Rig-wide common name prefix (e.g. 'mixamorig', 'CC_Base_'), detected from
 * the DATA — never from a platform table (ADR-004 §2.4). Returns the longest
 * normalized prefix shared by at least `minShare` of the names (default 0.8;
 * roots and props often escape the prefix), empty when none.
 */
export function commonNamePrefix(names: readonly string[], minShare = 0.8): string {
  if (names.length < 2) return '';
  const normalized = names.map(normalizeName);
  // Candidate prefixes come from EVERY name's token boundaries — an
  // unprefixed root ('Hips' before 'myStudio:*') must not blind the
  // detection (Codex, PR #14). Strict prefixes only: a candidate spanning a
  // whole name is that name, not a prefix of the family.
  const candidates = new Set<string>();
  for (const name of names) {
    const tokens = tokenize(name);
    for (let k = 1; k < tokens.length; k += 1) candidates.add(tokens.slice(0, k).join(''));
  }
  let best = '';
  for (const candidate of [...candidates].sort()) {
    const share = normalized.filter((n) => n.startsWith(candidate) && n.length > candidate.length).length / normalized.length;
    if (share >= minShare && candidate.length > best.length) best = candidate;
  }
  return best;
}
